import axios from 'axios';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';

// Client-driven S3 multipart upload for GB-sized files. The browser asks the
// backend to start an upload, gets one presigned URL per part, PUTs the parts
// directly to S3 in parallel (with retry), and tells the backend to complete.
//
// Requires S3 bucket CORS to allow PUT from the app origin AND to expose the
// `ETag` response header (the SDK/browser needs to read each part's ETag).
// See docs/infra/production-readiness-runbook.md §5.2.

// Anything at/above this goes multipart; smaller files use the single PUT path.
export const MULTIPART_THRESHOLD_BYTES = 16 * 1024 * 1024; // 16 MiB

const PART_SIZE_BYTES = Math.max(
  Number(import.meta.env.VITE_APP_S3_PART_SIZE_BYTES) || 16 * 1024 * 1024,
  5 * 1024 * 1024, // S3 minimum part size (except the last part)
);
const PART_CONCURRENCY = Math.max(1, Number(import.meta.env.VITE_APP_S3_PART_CONCURRENCY) || 4);
const PART_MAX_RETRIES = 3;

interface CompletedPart {
  PartNumber: number;
  ETag: string;
}

const computePartSize = (fileSize: number): number => {
  // S3 caps a multipart upload at 10,000 parts; grow the part size if needed.
  let size = PART_SIZE_BYTES;
  while (Math.ceil(fileSize / size) > 10000) size *= 2;
  return size;
};

const uploadPartWithRetry = async (url: string, blob: Blob, onChunk: (loaded: number) => void, signal?: AbortSignal): Promise<string> => {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= PART_MAX_RETRIES; attempt++) {
    try {
      let prev = 0;
      // Bare axios (NOT axiosInstance): the presigned S3 URL must not carry the
      // app's Authorization header or it breaks the SigV4 signature.
      const res = await axios.put(url, blob, {
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
        onUploadProgress: (e) => {
          // A part that already finished sending can still fire a trailing
          // progress event after abort() — ignore it so a cancelled upload
          // can't write state back and reopen the progress modal.
          if (signal?.aborted) return;
          onChunk(e.loaded - prev);
          prev = e.loaded;
        },
      });
      const etag = (res.headers?.etag || res.headers?.ETag) as string | undefined;
      if (!etag) {
        throw new Error('S3 did not return an ETag for the part (check bucket CORS ExposeHeaders).');
      }
      return etag;
    } catch (err) {
      lastErr = err;
      // Reset this part's progress contribution before retrying.
      onChunk(-blob.size);
      // Don't burn retries on a deliberate cancel — surface it immediately.
      if (signal?.aborted) throw err;
    }
  }
  throw lastErr;
};

/** Run thunks with a bounded concurrency pool, preserving result order. */
const runPool = async <T>(items: (() => Promise<T>)[], concurrency: number): Promise<T[]> => {
  const results: T[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    let i = next++;
    while (i < items.length) {
      results[i] = await items[i]();
      i = next++;
    }
  });
  await Promise.all(workers);
  return results;
};

export interface MultipartUploadArgs {
  file: File | Blob;
  key: string;
  contentType?: string;
  /** Called with overall 0-100 percent as parts complete. */
  onProgress?: (percent: number) => void;
  /** Aborts create/sign/part-PUT/complete calls when triggered. */
  signal?: AbortSignal;
}

export const uploadFileInParts = async ({ file, key, contentType, onProgress, signal }: MultipartUploadArgs): Promise<void> => {
  const { data: created } = await axiosInstance.post(
    ApiRoutes.S3_MULTIPART_CREATE,
    { key, contentType: contentType || (file as File).type || 'application/octet-stream' },
    { signal },
  );
  const uploadId: string = created.uploadId;

  const partSize = computePartSize(file.size);
  const partCount = Math.ceil(file.size / partSize);
  const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);

  try {
    const { data: signed } = await axiosInstance.post(ApiRoutes.S3_MULTIPART_SIGN_PARTS, { key, uploadId, partNumbers }, { signal });
    const urlByPart = new Map<number, string>(signed.parts.map((p: { partNumber: number; url: string }) => [p.partNumber, p.url]));

    let uploaded = 0;
    const reportProgress = (delta: number) => {
      uploaded = Math.max(0, Math.min(file.size, uploaded + delta));
      onProgress?.(Math.round((uploaded / file.size) * 100));
    };

    const thunks = partNumbers.map((partNumber) => async (): Promise<CompletedPart> => {
      const start = (partNumber - 1) * partSize;
      const blob = file.slice(start, Math.min(start + partSize, file.size));
      const url = urlByPart.get(partNumber);
      if (!url) throw new Error(`Missing presigned URL for part ${partNumber}`);
      const etag = await uploadPartWithRetry(url, blob, reportProgress, signal);
      return { PartNumber: partNumber, ETag: etag };
    });

    const parts = await runPool(thunks, PART_CONCURRENCY);

    await axiosInstance.post(ApiRoutes.S3_MULTIPART_COMPLETE, { key, uploadId, parts }, { signal });
    onProgress?.(100);
  } catch (err) {
    // Best-effort abort so S3 doesn't keep the orphaned parts around (the
    // bucket lifecycle rule is the backstop).
    try {
      await axiosInstance.post(ApiRoutes.S3_MULTIPART_ABORT, { key, uploadId });
    } catch {
      /* ignore — lifecycle rule will reap it */
    }
    throw err;
  }
};
